/**
 * 2026-08-25 — extracted verbatim from CandleBreakout.jsx, where it was
 * rendered with `hidden={true}` (i.e. never actually visible anywhere in
 * the app). That's the root cause behind "auto-login doesn't work — I have
 * to click Login to Upstox every day": the ONLY UI that ever wrote to the
 * `/upstox/auto-login/*` endpoints (the config upstox_autologin.py's
 * scheduled 03:35 IST job actually reads) was unreachable. The Settings
 * page's separate "Upstox API Credentials" TOTP/mobile/PIN fields save to
 * a different, currently-unused per-user store (`/me/upstox` — see that
 * form's own note) and were never wired to the real auto-login job, so
 * filling those in had no effect on the daily token refresh.
 *
 * Now rendered visibly in Settings (UserPages.jsx) so it's actually
 * reachable. No behavior change from the original component — same
 * endpoints, same test ids.
 */
import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { ChevronDown, ChevronRight, RefreshCw } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const UpstoxAutoLoginPanel = ({ onRefresh, hidden = false }) => {
  const [status, setStatus] = useState(null);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ mobile: "", pin: "", totp_secret: "" });

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/upstox/auto-login/status`);
      setStatus(r.data);
    } catch {
      /* ignore */
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (hidden) return null;

  const save = async () => {
    if (!form.mobile || !form.pin || !form.totp_secret) {
      toast.error("All three fields are required");
      return;
    }
    setBusy(true);
    try {
      await axios.post(`${API}/upstox/auto-login/config`, form);
      toast.success("Auto-login configured");
      setForm({ mobile: "", pin: "", totp_secret: "" });
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save");
    } finally {
      setBusy(false);
    }
  };

  const runNow = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/upstox/auto-login/run`);
      if (r.data?.ok) {
        toast.success("Auto-login succeeded — token refreshed");
      } else {
        toast.error(r.data?.error || "Auto-login failed");
      }
      await load();
      if (onRefresh) await onRefresh();
    } catch (e) {
      toast.error(e?.response?.data?.error || "Auto-login failed");
    } finally {
      setBusy(false);
    }
  };

  const clear = async () => {
    if (!window.confirm("Remove stored Upstox credentials?")) return;
    setBusy(true);
    try {
      await axios.delete(`${API}/upstox/auto-login/config`);
      toast.success("Auto-login cleared");
      await load();
    } finally {
      setBusy(false);
    }
  };

  const configured = !!status?.configured;
  const lastRunOk = status?.last_run_ok;

  return (
    <div className="border-grid surface" data-testid="cb-autologin-panel">
      <button
        data-testid="cb-autologin-toggle"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="w-full flex items-center justify-between px-4 py-2.5 hover:bg-white/[0.02]"
      >
        <span className="text-[11px] uppercase tracking-wider font-mono-data text-muted-2 inline-flex items-center gap-2">
          <RefreshCw size={13} />
          Upstox Auto-Login (TOTP)
          <span
            className="text-[9px] px-1.5 py-0.5 border ml-2"
            style={{
              color: configured ? (lastRunOk === false ? "#FFB66E" : "var(--bull)") : "var(--text-muted)",
              borderColor: configured ? (lastRunOk === false ? "#FFB66E" : "var(--bull)") : "var(--border-color)",
            }}
          >
            {configured ? (lastRunOk === false ? "LAST RUN FAILED" : "CONFIGURED") : "NOT CONFIGURED"}
          </span>
          {status?.mobile_masked && (
            <span className="text-muted-3">· {status.mobile_masked}</span>
          )}
        </span>
        {open ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
      </button>
      {open && (
        <div className="border-t border-grid px-4 py-4 space-y-3">
          <p className="text-[11px] text-muted-2 leading-relaxed">
            Auto-login uses the <strong>upstox-auth-pro</strong> library to perform
            a headless TOTP login at <strong>03:35 IST daily</strong>, so the token
            refreshes without your involvement. Credentials are encrypted at rest
            using a key derived from <code>UPSTOX_API_SECRET</code>.
          </p>
          <div className="border surface px-3 py-2 text-[10px]"
            style={{ borderColor: "#FFB66E", background: "rgba(255,182,110,0.06)", color: "#FFB66E" }}
          >
            <strong>⚠ Security note:</strong> your Upstox PIN and TOTP secret will be stored on
            this server. Use only for paper trading. To use this you must first enable TOTP
            2FA on your Upstox profile and copy the secret (not the 6-digit code) here.
          </div>

          {!configured && (
            <>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <label className="block">
                  <div className="text-[10px] uppercase tracking-wider text-muted-3 mb-1 font-mono-data">Mobile (10 digits)</div>
                  <input
                    data-testid="cb-autologin-mobile"
                    type="text"
                    autoComplete="off"
                    value={form.mobile}
                    onChange={(e) => setForm({ ...form, mobile: e.target.value })}
                    className="w-full border-grid surface px-2 py-1.5 text-xs font-mono-data"
                    placeholder="9876543210"
                  />
                </label>
                <label className="block">
                  <div className="text-[10px] uppercase tracking-wider text-muted-3 mb-1 font-mono-data">PIN (6 digit)</div>
                  <input
                    data-testid="cb-autologin-pin"
                    type="password"
                    autoComplete="new-password"
                    value={form.pin}
                    onChange={(e) => setForm({ ...form, pin: e.target.value })}
                    className="w-full border-grid surface px-2 py-1.5 text-xs font-mono-data"
                    placeholder="••••••"
                  />
                </label>
                <label className="block">
                  <div className="text-[10px] uppercase tracking-wider text-muted-3 mb-1 font-mono-data">TOTP Secret (Base32)</div>
                  <input
                    data-testid="cb-autologin-totp"
                    type="password"
                    autoComplete="new-password"
                    value={form.totp_secret}
                    onChange={(e) => setForm({ ...form, totp_secret: e.target.value })}
                    className="w-full border-grid surface px-2 py-1.5 text-xs font-mono-data"
                    placeholder="JBSWY3DPEHPK3PXP"
                  />
                </label>
              </div>
              <div className="flex items-center gap-2">
                <button
                  data-testid="cb-autologin-save"
                  onClick={save}
                  disabled={busy}
                  className="px-3 py-1.5 text-[10px] uppercase tracking-wider font-mono-data border border-grid hover:bg-white/5 disabled:opacity-50"
                >
                  Save Credentials
                </button>
              </div>
            </>
          )}

          {configured && (
            <div className="flex flex-wrap items-center gap-2">
              <button
                data-testid="cb-autologin-run"
                onClick={runNow}
                disabled={busy}
                className="px-3 py-1.5 text-[10px] uppercase tracking-wider font-mono-data border hover:bg-white/5 disabled:opacity-50 inline-flex items-center gap-1.5"
                style={{ borderColor: "var(--bull)", color: "var(--bull)" }}
              >
                <RefreshCw size={11} className={busy ? "animate-spin" : ""} />
                {busy ? "Logging in…" : "Run Auto-Login Now"}
              </button>
              <button
                data-testid="cb-autologin-clear"
                onClick={clear}
                disabled={busy}
                className="px-3 py-1.5 text-[10px] uppercase tracking-wider font-mono-data border border-grid hover:bg-white/5 disabled:opacity-50"
                style={{ color: "var(--bear)" }}
              >
                Remove Credentials
              </button>
              {status?.last_run_at && (
                <span className="text-[10px] text-muted-3 font-mono-data ml-auto">
                  Last run: {(status.last_run_at || "").slice(11, 16)} IST · {lastRunOk ? "✓" : "✗"}
                  {status.last_run_error && (
                    <span className="ml-2 text-muted-2" title={status.last_run_error}>
                      ({status.last_run_error.slice(0, 60)}…)
                    </span>
                  )}
                </span>
              )}
            </div>
          )}

          {/* 2026-08-25 — the third-party automation library only captured
              a debug screenshot on the very first possible failure point,
              so every later-step failure was an unexplained timeout. Our
              own copy of the flow (upstox_autologin.py) now captures a
              screenshot + page title/url at whichever step actually failed
              — surfaced here instead of leaving it buried in Mongo.
              _capture_debug runs unconditionally after PIN submission (not
              just on failure, so we can see where a successful run landed
              too) — gated to lastRunOk===false here so a successful run
              doesn't show a stale "stuck at" capture from a PRIOR failed
              attempt as if it were current. */}
          {status?.last_debug_step && lastRunOk === false && (
            <div className="border-t border-grid pt-3 mt-1 space-y-1.5">
              <div className="text-[10px] uppercase tracking-wider text-muted-3 font-mono-data">
                Last failure diagnostics
              </div>
              <div className="text-[10px] text-muted-2 font-mono-data leading-relaxed">
                Stuck at step: <strong style={{ color: "#FFB66E" }}>{status.last_debug_step}</strong>
                {status.last_debug_title && <> · page title: "{status.last_debug_title}"</>}
                {status.last_debug_at && <> · {(status.last_debug_at || "").slice(11, 16)} IST</>}
              </div>
              {status.last_debug_url && (
                <div className="text-[9px] text-muted-3 font-mono-data break-all">
                  URL: {status.last_debug_url}
                </div>
              )}
              {status.has_debug_screenshot && (
                <a
                  href={`${API}/upstox/auto-login/debug-screenshot`}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-block text-[10px] uppercase tracking-wider font-mono-data px-2 py-1 border hover:bg-white/5"
                  style={{ borderColor: "#7c4dff", color: "#b39dff" }}
                >
                  View debug screenshot →
                </a>
              )}
            </div>
          )}

          {/* 2026-08-26 — checking whether Upstox's OAuth dialog offers a
              TOTP-only login path that skips the SMS "Get OTP" trigger
              (the automation currently always clicks "Get OTP", which
              Upstox's own docs say sends an SMS regardless of TOTP being
              enabled — this is why a real OTP still lands on your phone).
              Shown regardless of last_run_ok since this is a one-shot,
              separately-stored capture, not tied to failure diagnostics. */}
          {status?.has_initial_page_screenshot && (
            <div className="border-t border-grid pt-3 mt-1 space-y-1.5">
              <div className="text-[10px] uppercase tracking-wider text-muted-3 font-mono-data">
                Initial login page (checking for a TOTP-only option)
              </div>
              <div className="text-[10px] text-muted-2 font-mono-data leading-relaxed">
                {status.initial_page_title && <>page title: "{status.initial_page_title}"</>}
                {status.initial_page_captured_at && <> · {(status.initial_page_captured_at || "").slice(11, 16)} IST</>}
              </div>
              <a
                href={`${API}/upstox/auto-login/initial-page-screenshot`}
                target="_blank"
                rel="noreferrer"
                className="inline-block text-[10px] uppercase tracking-wider font-mono-data px-2 py-1 border hover:bg-white/5"
                style={{ borderColor: "#7c4dff", color: "#b39dff" }}
              >
                View initial login page →
              </a>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default UpstoxAutoLoginPanel;
